"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

export interface SectionProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Section title */
  title?: React.ReactNode;
  /** Section description/subtitle */
  description?: React.ReactNode;
  /** Show divider after section */
  divider?: boolean;
  /** Custom header content (overrides title/description) */
  header?: React.ReactNode;
}

/**
 * Section - Section-level wrapper for page content
 *
 * Responsibilities:
 * - Consistent vertical spacing between sections
 * - Optional section dividers
 * - Heading slot for section titles
 */
const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      className,
      children,
      title,
      description,
      divider = false,
      header,
      ...props
    },
    ref
  ) => {
    const hasHeader = header || title || description;

    return (
      <section
        ref={ref}
        className={cn(styleDrivers.section.root, className)}
        {...props}
      >
        {hasHeader && (
          <div className={styleDrivers.section.header}>
            {header ? (
              header
            ) : (
              <>
                {title && (
                  <h2 className={styleDrivers.section.title}>{title}</h2>
                )}
                {description && (
                  <p className={styleDrivers.section.description}>
                    {description}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <div className={styleDrivers.section.content}>{children}</div>
        {divider && <div className={styleDrivers.section.divider} />}
      </section>
    );
  }
);

Section.displayName = "Section";

export { Section };
